/**
 * Display formatting helpers for USDT amounts, percentages, and time.
 *
 * USDT amounts come from the backend as strings (numeric(20,8)) so we
 * never lose precision. We render them with thin spaces as thousands
 * separators (CIS convention) and 2 decimal places.
 */
import BigNumber from 'bignumber.js';

const THIN_SPACE = '\u202F';

/**
 * Format a USDT amount for display. Accepts strings or numbers.
 *   formatUsdt('1234.56789') => '1\u202F234.56'
 */
export function formatUsdt(value: string | number, decimals = 2): string {
  const bn = new BigNumber(value);
  if (bn.isNaN() || !bn.isFinite()) return '—';
  const fixed = bn.decimalPlaces(decimals, BigNumber.ROUND_DOWN).toFixed(decimals);
  const [intPart, rawDec] = fixed.split('.');
  // Убираем trailing zeros: "100.00" → "100", "12.50" → "12.5"
  const decPart = rawDec?.replace(/0+$/, '');
  const intWithSeparators = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, THIN_SPACE);
  return decPart ? `${intWithSeparators}.${decPart}` : intWithSeparators;
}

/**
 * Format a percentage from a fraction.
 *   formatPercent(0.0345) => '3.45%'
 */
export function formatPercent(fraction: string | number, decimals = 2): string {
  const bn = new BigNumber(fraction).multipliedBy(100);
  if (bn.isNaN()) return '—';
  return `${bn.toFixed(decimals)}%`;
}

/**
 * Compact form for big numbers.
 *   formatCompact(1234567) => '1.23M'
 */
export function formatCompact(value: string | number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const absN = Math.abs(n);
  if (absN >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (absN >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (absN >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toFixed(2);
}

/**
 * СНГ формат числа: без лишних нулей, макс 3 знака после точки.
 * 100.00 → "100", 12.50 → "12.5", 0.001 → "0.001", 0 → "0"
 */
export function fmtNum(v: number | string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  // Округляем до 3 знаков и убираем trailing zeros
  return parseFloat(n.toFixed(3)).toString();
}

/**
 * Relative time (e.g. '2m ago'). Суффиксы зависят от языка UI.
 *   ru → с, м, ч, д
 *   en → s, m, h, d
 */
export function formatRelativeTime(iso: string, lang: 'ru' | 'en' = 'ru'): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (Number.isNaN(diffMs)) return '—';
  const suffix = lang === 'ru'
    ? { s: 'с', m: 'м', h: 'ч', d: 'д' }
    : { s: 's', m: 'm', h: 'h', d: 'd' };
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}${suffix.s}`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}${suffix.m}`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}${suffix.h}`;
  const days = Math.floor(hr / 24);
  return `${days}${suffix.d}`;
}

/**
 * Локализованная дата.
 *   ru → "DD.MM.YYYY"  (14.04.2026)
 *   en → "MM/DD/YYYY"  (04/14/2026)
 */
export function formatDateLocalized(iso: string, lang: 'ru' | 'en' = 'ru'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return lang === 'ru' ? `${dd}.${mm}.${yyyy}` : `${mm}/${dd}/${yyyy}`;
}

/**
 * Локализованный числовой формат:
 *   ru → пробел для тысяч, "." десятичный  (10 000.5)
 *   en → запятая для тысяч, "." десятичный (10,000.5)
 * Убирает trailing zeros, максимум 3 знака после точки.
 */
export function formatNumberLocalized(
  v: number | string,
  lang: 'ru' | 'en' = 'ru',
): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  const rounded = parseFloat(n.toFixed(3));
  const [intPart, decPart] = rounded.toString().split('.');
  const sep = lang === 'ru' ? '\u202F' : ',';
  const intWithSep = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return decPart ? `${intWithSep}.${decPart}` : intWithSep;
}
